// @ts-check
// @ts-ignore
// @ts-ignore
import { Component } from '@verndale/core';
import YouTubePlayer from 'yt-player';
import VimeoPlayer from '@vimeo/player';
import LimeLightPlayer from '../limelight';
import { setTrackDataLayer, secondsToHHMMSS } from '../utils';

// @ts-ignore
const VIDEO_EVENT = 'video_interactions';

class VideoItem extends Component {
  setupDefaults() {
    this.dom = {
      $imageContainer: this.el.querySelector('.card__image-container'),
      $playButton: this.el.querySelector('.video-item__icon-container'),
      $youTubePlayer: this.el.querySelector('.video-item__youtube-video'),
      $vimeoPlayer: this.el.querySelector('.video-item__vimeo-video'),
      $limelightPlayer: this.el.querySelector('.video-item__limelight-video'),
      $mediaModal: document.getElementById('modal-1')
    };

    this.nativePlayer = this.el.dataset.native;

    this.loaded = false;
    this.init();
    this.initObserver();
  }

  addListeners() {
    this.dom?.$playButton?.addEventListener('click', () => this.playVideo());
    this.dom?.$playButton?.addEventListener('mouseover', () =>
      this.loadVideo()
    );
  }

  observeClass(mutationsList) {
    mutationsList.forEach((mutation) => {
      if (mutation.attributeName === 'class') {
        if (!mutation.target.classList.contains('is-open')) {
          // @ts-ignore
          this.player?.stop();
          this.playerVimeo?.unload();
        }
      }
    });
  }

  switchToVideo() {
    if (this.dom) {
      this.dom.$imageContainer.style.display = 'none';
      this.dom.$playButton.style.display = 'none';
    }
    this.el.classList.add('video-item--playing');
  }

  playVideo() {
    this.switchToVideo();
    this.player?.play();
    this.playerVimeo?.play();
  }

  pauseVideo() {
    this.player?.pause();
    this.playerVimeo?.pause();
  }

  loadVideo() {
    if (!this.loaded) {
      this.player?.load(this.videoId);
      this.loaded = true;
    }
  }

  async getVideoMetadata() {
    if (this.videoProps) {
      if (this.el.dataset.youtubeId) {
        this.player = new YouTubePlayer(this.dom?.$youTubePlayer);
        this.videoId = this.el.dataset.youtubeId;
        // @ts-ignore
        this.videoProps.video_id = this.videoId;

        // These are all the events that the YouTube player can trigger (https://developers.google.com/youtube/iframe_api_reference#Events)
        const ytPlayerEvents = ['unstarted', 'ended', 'playing', 'paused'];

        ytPlayerEvents.forEach((event) => {
          // @ts-ignore
          this.player?.on(event, async () => {
            // @ts-ignore
            this.videoProps.video_duration = secondsToHHMMSS(
              // @ts-ignore
              this.player?.getDuration().toString()
            );
            // @ts-ignore
            this.videoProps.video_current_time = secondsToHHMMSS(
              // @ts-ignore
              this.player?.getCurrentTime().toString()
            );
            // @ts-ignore
            this.videoProps.video_progress_percentage =
              // @ts-ignore
              `${(this.player?.getProgress() * 100).toFixed(0)}%`;

            setTrackDataLayer({
              eventName: VIDEO_EVENT,
              custom: {
                interaction: event,
                ...this.videoProps
              }
            });
          });
        });
      }

      if (this.dom?.$vimeoPlayer) {
        const vimeoVideoId = this.dom?.$vimeoPlayer?.dataset?.vimeoId;
        this.playerVimeo = new VimeoPlayer(this.dom?.$vimeoPlayer, {
          id: vimeoVideoId,
          dnt: true
        });
        this.videoId = vimeoVideoId;
        // @ts-ignore
        this.videoProps.video_id = this.videoId;

        let retries = 0;
        const enforceVimeoDNT = () => {
          const iframe = this.dom?.$vimeoPlayer?.querySelector('iframe');
          if (iframe) {
            if (!iframe.src.includes('dnt=1')) {
              const separator = iframe.src.includes('?') ? '&' : '?';
              iframe.src = `${iframe.src}${separator}dnt=1`;
            }
          } else if (retries < 10) {
            retries += 1;
            setTimeout(enforceVimeoDNT, 100);
          }
        };
        enforceVimeoDNT();

        // These are all the events that the Vimeo player can trigger (https://developer.vimeo.com/player/sdk/reference#events-for-playback-controls)
        const vimeoPlayerEvents = ['ended', 'error', 'pause', 'play'];

        vimeoPlayerEvents.forEach((event) => {
          // @ts-ignore
          this.playerVimeo.on(event, async () => {
            // @ts-ignore
            const videoDuration = await this.playerVimeo.getDuration();
            // @ts-ignore
            const videoCurrentTime = await this.playerVimeo.getCurrentTime();
            // @ts-ignore
            this.videoProps.video_title =
              // @ts-ignore
              await this.playerVimeo.getVideoTitle();
            // @ts-ignore
            this.videoProps.video_duration = secondsToHHMMSS(
              videoDuration.toString()
            );
            // @ts-ignore
            this.videoProps.video_current_time = secondsToHHMMSS(
              videoCurrentTime.toString()
            );
            // @ts-ignore
            this.videoProps.video_progress_percentage = `${Math.round(
              (videoCurrentTime * 100) / videoDuration
            )}%`;

            // Do not track pause event if the video is just starting or closed
            if (videoCurrentTime > 0 || event !== 'pause') {
              setTrackDataLayer({
                eventName: VIDEO_EVENT,
                custom: {
                  interaction: event,
                  ...this.videoProps
                }
              });
            }
          });
        });
      }
    }
  }

  async init() {
    this.videoProps = {
      video_title: '', // Video title
      video_duration: '', // Duration in seconds
      video_current_time: '', // Current time in seconds
      video_id: '', // Unique video ID
      video_progress_percentage: '' // Percentage of video watched, (Just for those 4 percentages)
    };

    if (this.el.dataset.limelightId && !this.nativePlayer) {
      this.player = new LimeLightPlayer({ parent: this.dom?.$limelightPlayer });
      this.videoId = this.el.dataset.limelightId;
    }

    await this.getVideoMetadata();

    if (this.nativePlayer) {
      if (this.el.dataset.limelightId) {
        this.player = new LimeLightPlayer({
          settings: { mediaId: this.el.dataset.limelightId },
          parent: this.dom?.$limelightPlayer
        });
      } else {
        this.loadVideo();
      }
    }
  }

  initObserver() {
    if (this.dom?.$mediaModal) {
      const mutationObserver = new MutationObserver(
        this.observeClass.bind(this)
      );
      mutationObserver.observe(this.dom.$mediaModal, { attributes: true });
    }
  }
}

export default VideoItem;
